import ReviewAudit from 'components/ReviewAudit'
import PlayerInvitationPanel from './PlayerInvitationPanel'
import Tenis10ChildrenPanel, { useAccountChildren } from '../../../Tenis10Claims/Tenis10ChildrenPanel'
import { childrenText, holdsPersona } from '../personas'
import { activePassports, profileTabs } from './profileTabs'
import React, { useEffect, useState, useContext } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Link, withRouter } from "react-router-dom"
import moment from "moment"
import { isEmpty, capitalize } from "lodash"
import Select from "react-select"
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Nav,
  NavLink,
  NavItem,
  Row,
  Spinner,
  TabContent,
  Table,
  TabPane,
  FormGroup,
  Label,
} from "reactstrap"
import { AvField, AvForm } from "availity-reactstrap-validation"
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
} from "react-bootstrap-table2-paginator"

import ToolkitProvider from "react-bootstrap-table2-toolkit"
import BootstrapTable from "react-bootstrap-table-next"
import SweetAlert from "react-bootstrap-sweetalert"
import toastr from "toastr"
import classnames from "classnames"

import defaultProfilePicture from "../../../../assets/images/users/default-profile-picture.png"
import Breadcrumbs from "components/Common/Breadcrumb"
import RatingsModal from "./ratingsModal"
import PlayerReputationSummary from "./PlayerReputationSummary"
import OpponentLevelAnalysis from './OpponentLevelAnalysis'
import ProfileLoadError from "../../ProfileLoadError"
import {
  ProfileOverviewCard,
  SportsSummaryCard,
} from "./profileSummaryCards"
import activityColumns from "./activityColumns"
import entriesColumns, {
  countPlayerEvents,
  getTournamentEntryRowClass,
  hasChildEntries,
  mergeTournamentEntries,
} from "./entriesColumns"
import membershipColumns from "./membershipColumns"
import orderColumns from "./orderColumns"
import {
  buildOrderQueryOptions,
  orderCategoryOptions,
} from "./orderFilters"
import levelChangeColumns from "./levelChangeColumns"
import {
  getWalletAdjustmentAmount,
  getWalletBalancePreview,
} from "./walletAdjustment"
import {
  getPlayerInfo,
  getPlayerTransactions,
  getPlayerWalletTransactions,
  getMembershipList,
  getPlayerActivity,
  getLevelChange,
  getPlayerEntries,
  getPlayerHistoryEntries,
  getPlayerReferralRewards,
  getPlayerFirstTaskRewards,
  resetPlayerProfileData,
} from "../actions"
import {
  updateStatus,
  updatePlayerLevel,
  updatePlayerWallet,
  updatePlayerMembership,
} from "../api"
import ReportCount from "../../../ReportsManagement/ReportCount"

import { store as socketStore } from "../../../../components/Common/Socket/store"
import PlayerImpersonateAction from "../PlayerImpersonateAction"
import { getCurrentPlayerProfileIds } from "./profileGuards"
import { withFloatingSurface } from 'components/Common/selectSurface'
import "../../../../assets/scss/custom/pages/_playerAccounts.scss"

// The header as on the organizer's and the club's profiles: the identity on one row with the actions at its right, then four
// labelled groups across the card (Contact · Personal · Account · Membership & activity).
const statusColor = status => {
  const normalizedStatus = String(status || "").toLowerCase()

  if (["activated", "active", "created", "enabled"].includes(normalizedStatus)) {
    return "success"
  }
  if (normalizedStatus === "pending") return "info"
  if (["blocked", "disabled"].includes(normalizedStatus)) return "warning"
  return "secondary"
}

// One of the header's four labelled groups across the card's width.
const HeaderFact = ({ children, label }) => (
  <section className="player-profile-fact">
    <h4 className="player-profile-fact-label">{label}</h4>
    {children}
  </section>
)

HeaderFact.propTypes = {
  children: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
}

// A labelled value inside a group, two to a row; „—” when the account has none.
const HeaderField = ({ children, label, wide }) => (
  <div className={`player-profile-field${wide ? " is-wide" : ""}`}>
    <div className="player-profile-field-label">{label}</div>
    <div className="player-profile-field-value">{children ?? "—"}</div>
  </div>
)

HeaderField.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string.isRequired,
  wide: PropTypes.bool,
}

HeaderField.defaultProps = {
  children: null,
  wide: false,
}

const FactLine = ({ children, icon, muted }) => (
  <div className={`player-profile-contact-link${muted ? " text-muted" : ""}`}>
    <i className={`bx ${icon}`} aria-hidden="true" />
    <span>{children}</span>
  </div>
)

FactLine.propTypes = {
  children: PropTypes.node.isRequired,
  icon: PropTypes.string.isRequired,
  muted: PropTypes.bool,
}

FactLine.defaultProps = {
  muted: false,
}

// A long address breaks before its @ first, never mid-word.
const EmailText = ({ email }) => {
  const at = email.lastIndexOf("@")

  if (at <= 0) return email

  return (
    <>
      {email.slice(0, at)}
      <wbr />
      {email.slice(at)}
    </>
  )
}

EmailText.propTypes = {
  email: PropTypes.string.isRequired,
}

const valueOrNull = (value, format = text => text) => (value ? format(value) : null)

const PlayerProfile = props => {
  const {
    getPlayerInfo,
    playerInfo,
    getPlayerTransactions,
    getPlayerWalletTransactions,
    getMembershipList,
    getPlayerActivity,
    getLevelChange,
    loading,
    profileError,
    playerTransactions,
    playerWalletTransactions,
    membershipList,
    playerActivity,
    levelChange,
    fetchPlayerEntries,
    playerEntries,
    fetchPlayerHistoryEntries,
    playerHistoryEntries,
    match: { params },
    settings,
    referralRewards,
    firstTaskRewards,
    fetchPlayerReferralRewards,
    fetchPlayerFirstTaskRewards,
    resetPlayerProfileData,
  } = props

  const {
    accountId: currentPlayerAccountId,
    userId: currentPlayerUserId,
  } = getCurrentPlayerProfileIds(playerInfo, params.id)

  // PRD 16 LR16 (LR10 verdict U-B) — what the account is (ADR-0012) and its Tenis10 children: one read for the tab, its count,
  // the header and the children summary
  const childrenRead = useAccountChildren(currentPlayerAccountId)
  // the panel's rows; the children among them — never the account holder's own 16+ history — are what the page counts
  const accountRows = childrenRead.state.status === "ready" ? childrenRead.state.data.children : null
  const accountChildren = accountRows && accountRows.filter(row => !row.adult)
  const holdsPlayer = holdsPersona(playerInfo, "player")
  const holdsParent = holdsPersona(playerInfo, "parent")
  // the children tab stays on the page once it showed a child: the action that took the last one away keeps its outcome there
  const [childrenTabKept, setChildrenTabKept] = useState(null)
  useEffect(() => {
    if (accountRows?.length) setChildrenTabKept(currentPlayerAccountId)
  }, [accountRows, currentPlayerAccountId])

  const pageWalletTransactionsOptions = {
    page: playerWalletTransactions?.page,
    sizePerPage: playerWalletTransactions?.limit,
    totalSize: playerWalletTransactions?.totalResults,
    custom: true,
    hidePageListOnlyOnePage: true,
  }

  const pageTransactionsOptions = {
    page: playerTransactions?.page,
    sizePerPage: playerTransactions?.limit,
    totalSize: playerTransactions?.totalResults,
    custom: true,
    hidePageListOnlyOnePage: true,
  }

  const pageMembershipOptions = {
    page: membershipList?.page,
    sizePerPage: membershipList?.limit,
    totalSize: membershipList?.totalResults,
    custom: true,
    hidePageListOnlyOnePage: true,
  }

  const pagePlayerActivityOptions = {
    page: playerActivity?.page,
    sizePerPage: playerActivity?.limit,
    totalSize: playerActivity?.totalResults,
    custom: true,
    hidePageListOnlyOnePage: true,
  }

  const pageLevelChangeOptions = {
    page: levelChange?.page,
    sizePerPage: levelChange?.limit,
    totalSize: levelChange?.totalResults,
    custom: true,
    hidePageListOnlyOnePage: true,
  }

  const initialOptions = {
    page: 1,
    limit: 10,
  }

  const [blockAlert, setBlockAlert] = useState(false)
  const [deleteAlert, setDeleteAlert] = useState(false)
  const [activateAlert, setActivateAlert] = useState(false)
  const [successAlert, setSuccessAlert] = useState(false)
  const [alertTitle, setAlertTitle] = useState()
  const [alertDescription, setAlertDescription] = useState()
  // the tab the admin chose; until then (and on another user's page) the user's first tab
  const [activeTab, setActiveTab] = useState(null)
  const [walletBalance, setWalletBalance] = useState("")
  const [walletAmount, setWalletAmount] = useState("")
  const [walletOperation, setWalletOperation] = useState("add")
  const [editMemberShip, setEditMemberShip] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const globalState = useContext(socketStore)
  const { state: { socket: { socketClientId, socket } = {} } = {} } =
    globalState
  const [options, setOptions] = useState(initialOptions)
  const [transactionsOptions, setTransactionsOptions] = useState(initialOptions)
  const [transactionCategory, setTransactionCategory] = useState("all")
  const [membershipOptions, setMembershipOptions] = useState(initialOptions)
  const [playerActivityOptions, setPlayerActivityOptions] =
    useState(initialOptions)
  const [levelChangeOptions, setLevelChangeOptions] = useState(initialOptions)
  const [playerEntriesOptions] = useState(initialOptions)
  const [playerHistoryEntriesOptions] = useState(initialOptions)
  const [membershipToChoose, setMembershipToChoose] = useState(null)
  const [activeCompetitonRegisteredAlert, setActiveCompetitonRegisteredAlert] =
    useState(false)
  const [viewRatingsModal, setViewRatingsModal] = useState(false)

  // A Tenis10 child's entry rides on this account: its events are counted apart, and they are no reason to confirm the
  // account's own level change
  const currentEntriesCount = countPlayerEvents(playerEntries, currentPlayerUserId)
  const historyEntriesCount = countPlayerEvents(
    playerHistoryEntries,
    currentPlayerUserId
  )
  const entriesCountText = (counts, label) =>
    `${counts.own} ${label}${
      counts.children ? ` + ${counts.children} for children` : ""
    }`
  const hasOwnActiveEntries = currentEntriesCount.own > 0
  // one row per participant: the player's own entries and each Tenis10 child's, named in a Participant column when there is one
  const tournamentEntries = mergeTournamentEntries(
    playerEntries,
    playerHistoryEntries,
    playerInfo?.userId
  )
  const tournamentColumns = entriesColumns(playerInfo?.userId, {
    participants: hasChildEntries(tournamentEntries),
  })
  const pageTournamentEntriesOptions = {
    sizePerPage: 10,
    totalSize: tournamentEntries.length,
    hidePageListOnlyOnePage: true,
  }

  const openRatingsModal = () => {
    if (props.userRole === "superAdmin") setViewRatingsModal(true)
  }
  const closeRatingsModal = () => setViewRatingsModal(false)

  const { tabs: userTabs, defaultTab } = profileTabs({ account: playerInfo, rows: accountRows, children: accountChildren,
    keepChildren: childrenRead.state.status === "error" || childrenTabKept === currentPlayerAccountId })
  const shownTab = userTabs.some(tab => tab.id === activeTab) ? activeTab : defaultTab
  const passportsInForce = activePassports(accountChildren)
  // „3 · 2 passports active”
  const childrenSummary = accountChildren && `${accountChildren.length} · ${passportsInForce} ${passportsInForce === 1 ? "passport" : "passports"} active`

  function toggleTab(tab) {
    if (activeTab !== tab) {
      setActiveTab(tab)
    }
  }

  const handleBlock = async () => {
    const result = await updateStatus(params.id, "blocked")

    if (result?.success) {
      setBlockAlert(false)
      setSuccessAlert(true)
      setAlertTitle("Blocked")
      setAlertDescription("Account was successfully blocked.")
      getPlayerInfo(params.id)
    } else {
      toastr.error(result?.message || "Player could not be blocked.")
    }
  }

  const handleUpdateMembership = async () => {
    if (!editMemberShip?.value) return

    setIsLoading(true)

    const response = await updatePlayerMembership(params.id, {
      socketClientId,
      membership: editMemberShip.value,
    })

    if (!response?.success) {
      toastr.error(response?.message || "Membership could not be updated.")
      setIsLoading(false)
    } else {
      setEditMemberShip(null)
    }

    setIsLoading(false)
  }

  const handleDelete = async () => {
    const result = await updateStatus(params.id, "deleted")

    if (result?.success) {
      setDeleteAlert(false)
      setSuccessAlert(true)
      setAlertTitle("Deleted")
      setAlertDescription("Account was successfully deleted.")
      getPlayerInfo(params.id)
    } else {
      toastr.error(result?.message || "Player could not be deleted.")
    }
  }

  const handleActivate = async () => {
    const result = await updateStatus(params.id, "activated")

    if (result?.success) {
      setActivateAlert(false)
      setSuccessAlert(true)
      setAlertTitle("Activated")
      setAlertDescription("Account was successfully activated.")
      getPlayerInfo(params.id)
    } else {
      toastr.error(result?.message || "Player could not be activated.")
    }
  }

  const handleWalletBalance = event => {
    setWalletBalance(event.target.value)
    setWalletAmount(
      event.target.value === "custom" ? "" : event.target.value
    )
  }

  const handleCustomWalletBalance = event => {
    setWalletAmount(event.target.value)
  }

  const handleBalanceSubmit = async (event, values) => {
    const adjustmentAmount = getWalletAdjustmentAmount(
      walletOperation,
      walletAmount
    )

    if (adjustmentAmount === null) {
      toastr.error("Enter an amount greater than zero.")
      return
    }

    setIsLoading(true)

    const response = await updatePlayerWallet(params.id, {
      socketClientId,
      amount: adjustmentAmount,
      currency: playerInfo.currency,
      notes: values?.addNotes,
    })

    if (!response?.success) {
      toastr.error(response?.message || "Balance could not be updated.")
      setIsLoading(false)
    } else {
      setWalletAmount("")
      setWalletBalance("")
    }

    setIsLoading(false)
  }

  const handleLevelChange = async (event, values) => {
    setIsLoading(true)

    const response = await updatePlayerLevel(params.id, {
      socketClientId,
      sportType,
      gameLevel: {
        singles:
          selectedSoloLevel ??
          playerInfo.sports?.[sportType]?.singles?.gameLevel,
        doubles:
          selectedDuoLevel ??
          playerInfo.sports?.[sportType]?.doubles?.gameLevel,
      },
    })

    if (!response?.success) {
      toastr.error(response?.message || "Level could not be updated.")
      setIsLoading(false)
    }

    setIsLoading(false)
  }

  const handleTableChange = (type, data) => {
    switch (type) {
      case "pagination":
        setOptions({
          ...options,
          page: data.page,
        })
        break
      default:
        break
    }
  }

  const handleTransactionTableChange = (type, data) => {
    switch (type) {
      case "pagination":
        setTransactionsOptions({
          ...transactionsOptions,
          page: data.page,
        })
        break
      default:
        break
    }
  }

  const handleTransactionCategoryChange = event => {
    const category = event.target.value

    setTransactionCategory(category)
    setTransactionsOptions(currentOptions =>
      buildOrderQueryOptions(category, currentOptions)
    )
  }

  const handleActivityTableChange = (type, data) => {
    switch (type) {
      case "pagination":
        setPlayerActivityOptions({
          ...playerActivityOptions,
          page: data.page,
        })
        break
      default:
        break
    }
  }

  const handleLevelChangeChange = (type, data) => {
    switch (type) {
      case "pagination":
        setLevelChangeOptions({
          ...levelChangeOptions,
          page: data.page,
        })
        break
      default:
        break
    }
  }

  const handleMembershipTableChange = (type, data) => {
    switch (type) {
      case "pagination":
        setMembershipOptions({
          ...membershipOptions,
          page: data.page,
        })
        break
      default:
        break
    }
  }

  useEffect(() => {
    resetPlayerProfileData()
    getPlayerInfo(params.id)
    setActiveTab(null)
  }, [resetPlayerProfileData, getPlayerInfo, params.id])

  useEffect(() => {
    if (currentPlayerUserId) {
      getPlayerTransactions(currentPlayerUserId, transactionsOptions)
      fetchPlayerEntries(currentPlayerUserId, playerEntriesOptions)
    }
  }, [
    getPlayerTransactions,
    currentPlayerUserId,
    fetchPlayerEntries,
    transactionsOptions,
    playerEntriesOptions,
  ])

  useEffect(() => {
    if (currentPlayerUserId) {
      fetchPlayerHistoryEntries(currentPlayerUserId, playerHistoryEntriesOptions)
    }
  }, [
    currentPlayerUserId,
    fetchPlayerHistoryEntries,
    playerHistoryEntriesOptions,
  ])

  useEffect(() => {
    if (currentPlayerUserId) {
      getPlayerWalletTransactions(currentPlayerUserId, options)
    }
  }, [currentPlayerUserId, getPlayerWalletTransactions, options])

  useEffect(() => {
    if (currentPlayerUserId) {
      fetchPlayerReferralRewards(currentPlayerUserId)
    }
  }, [currentPlayerUserId, fetchPlayerReferralRewards])

  useEffect(() => {
    if (currentPlayerUserId) {
      fetchPlayerFirstTaskRewards(currentPlayerUserId)
    }
  }, [currentPlayerUserId, fetchPlayerFirstTaskRewards])

  useEffect(() => {
    if (currentPlayerUserId) {
      getMembershipList(currentPlayerUserId, membershipOptions)
    }
  }, [currentPlayerUserId, getMembershipList, membershipOptions])

  useEffect(() => {
    if (currentPlayerAccountId) {
      getPlayerActivity(currentPlayerAccountId, playerActivityOptions)
    }
  }, [currentPlayerAccountId, getPlayerActivity, playerActivityOptions])

  useEffect(() => {
    if (currentPlayerAccountId) {
      getLevelChange(currentPlayerAccountId, levelChangeOptions)
    }
  }, [currentPlayerAccountId, getLevelChange, levelChangeOptions])

  const [sportType, setSportType] = useState("tennis")
  const [selectedSoloLevel, setSelectedSoloLevel] = useState(
    playerInfo?.sports?.[sportType]?.singles?.gameLevel
  )
  const [selectedDuoLevel, setSelectedDuoLevel] = useState(
    playerInfo?.sports?.[sportType]?.doubles?.gameLevel
  )

  useEffect(() => {
    if (!isEmpty(playerInfo) && sportType) {
      setSelectedSoloLevel(playerInfo.sports?.[sportType]?.singles?.gameLevel)
      setSelectedDuoLevel(playerInfo.sports?.[sportType]?.doubles?.gameLevel)
    }
  }, [playerInfo, sportType])

  useEffect(() => {
    const listenForResponse = async ({ success, message, operation }) => {
      if (operation) return
      if (!success) {
        toastr.error(message)
        setIsLoading(false)
        return
      }
      toastr.success(message)
      getPlayerInfo(params.id)
      if (currentPlayerUserId) {
        getMembershipList(currentPlayerUserId, membershipOptions)
        getPlayerWalletTransactions(currentPlayerUserId, options)
      }
      if (currentPlayerAccountId) {
        getLevelChange(currentPlayerAccountId, levelChangeOptions)
      }
    }

    if (socket) {
      socket.on("accounts-response", listenForResponse)

      return () => {
        socket.off("accounts-response", listenForResponse)
      }
    }
  }, [socketClientId]) // eslint-disable-line

  useEffect(() => {
    const membershipPlans = [...(settings?.memberships || [])].sort(
      (a, b) => a?.activeDays - b?.activeDays
    )
    const membs = [
      {
        label: "Free",
        value: {
          activeDays: 0,
          id: null,
          name: "free",
          plan: "free",
          type: "free",
        },
      },
      ...membershipPlans.map((plan, key) => ({
        label: (
          <div key={key}>
            {plan?.name?.split("-")[0].charAt(0).toUpperCase() +
              plan?.name?.split("-")[0].slice(1) +
              " " +
              plan?.name?.split("-")[1] +
              " " +
              `(${plan?.activeDays} days)`}
          </div>
        ),
        value: plan,
      })),
    ]

    setMembershipToChoose(membs)
  }, [settings])


  const totalReferralRewards = (referralRewards.results || []).reduce(
    (total, reward) => total + reward.amount,
    0
  )
  const totalFirstTaskRewards = (firstTaskRewards.results || []).reduce(
    (total, reward) => total + reward.amount,
    0
  )
  const currentMembershipPlan = playerInfo?.membership?.plan || "free"
  const membershipEndDate = playerInfo?.membership?.endDate
  const membershipIsActive =
    currentMembershipPlan !== "free" &&
    membershipEndDate &&
    moment(membershipEndDate).isAfter(moment())
  const membershipStatus =
    currentMembershipPlan === "free"
      ? "Free plan"
      : membershipIsActive
      ? "Active"
      : "Expired"
  const membershipDaysRemaining = membershipIsActive
    ? Math.max(0, moment(membershipEndDate).diff(moment(), "days"))
    : null
  const walletAdjustmentAmount = getWalletAdjustmentAmount(
    walletOperation,
    walletAmount
  )
  const walletBalancePreview = getWalletBalancePreview(
    playerInfo?.walletBalance,
    walletAdjustmentAmount
  )
  const currentSinglesLevel =
    playerInfo?.sports?.[sportType]?.singles?.gameLevel
  const currentDoublesLevel =
    playerInfo?.sports?.[sportType]?.doubles?.gameLevel
  const levelHasChanges =
    selectedSoloLevel !== currentSinglesLevel ||
    selectedDuoLevel !== currentDoublesLevel

  return (
    <React.Fragment>
      <div className="page-content player-profile-page">
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs
            title="Users"
            path={{ pathname: "/accounts/players", search: props.location?.state?.playerListSearch || "", state: props.location?.state }}
            breadcrumbItem="User Profile"
          />
          {profileError ? (
            <ProfileLoadError title="Unable to load user profile" profile="user profile" status={profileError.status}
              onRetry={() => getPlayerInfo(params.id)} />
          ) : loading || isLoading || !currentPlayerAccountId ? (
            <div className="text-center">
              <Spinner className="mt-5 mb-5" color="primary" />
            </div>
          ) : (
            <>
              <Card className="player-profile-hero">
                <CardBody>
                  <div className="player-profile-hero-main">
                    <div className="player-profile-identity">
                      <img
                        src={`${
                          playerInfo.profilePicture
                            ? `${process.env.REACT_APP_API_URL}/assets/accounts/${playerInfo.userId}/${playerInfo.profilePicture}`
                            : defaultProfilePicture
                        }`}
                        alt={`${playerInfo.firstName} ${playerInfo.lastName}`}
                        className="player-profile-avatar"
                      />
                      <div className="min-w-0">
                        {/* a player account is a user; what it does is in its personas (ADR-0012) */}
                        <div className="player-profile-eyebrow">
                          {playerInfo.role === "player" ? "User" : capitalize(playerInfo.role)} #{playerInfo.userId}
                        </div>
                        <h3 className="player-profile-name">
                          {playerInfo.firstName} {playerInfo.lastName}
                        </h3>
                        <div className="player-profile-badges">
                          <Badge color={statusColor(playerInfo.status)}>
                            {capitalize(playerInfo.status || "unknown")}
                          </Badge>
                          {holdsPlayer && <Badge color="soft-primary">Player</Badge>}
                          {holdsParent && <Badge color="soft-info">
                            Parent{accountChildren ? ` · ${childrenText(accountChildren.length)}` : ""}
                          </Badge>}
                        </div>
                      </div>
                    </div>

                    {/* a deleted account keeps its identity, without actions */}
                    {playerInfo.status !== "deleted" && (
                      <div className="player-profile-actions">
                        <a
                          className="btn btn-primary waves-effect waves-light btn-md"
                          href={`${process.env.REACT_APP_WEB_URL}/accounts/${playerInfo.id}`}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          View Profile
                        </a>
                        <PlayerImpersonateAction account={playerInfo} />
                        {playerInfo.status !== "blocked" && (
                          <Button
                            className="btn btn-warning waves-effect waves-light btn-md"
                            onClick={() => {
                              setBlockAlert(true)
                            }}
                          >
                            Block
                          </Button>
                        )}
                        <Button
                          className="btn btn-danger waves-effect waves-light btn-md"
                          onClick={() => {
                            setDeleteAlert(true)
                          }}
                        >
                          Delete
                        </Button>
                        {playerInfo.status !== "activated" && (
                          <Button
                            className="btn btn-success waves-effect waves-light btn-md"
                            onClick={() => {
                              setActivateAlert(true)
                            }}
                          >
                            Activate
                          </Button>
                        )}
                        <Link
                          to={{ pathname: `/accounts/players/${playerInfo.id}/edit`, state: props.location?.state }}
                          className="btn btn-light waves-effect waves-light btn-md"
                        >
                          Edit
                        </Link>
                      </div>
                    )}
                  </div>

                  <div className="player-profile-facts">
                    <HeaderFact label="Contact">
                      {playerInfo.email ? (
                        // the <wbr> would put a space in the link's name: the address is its name
                        <a
                          className="player-profile-contact-link"
                          href={`mailto:${playerInfo.email}`}
                          aria-label={playerInfo.email}
                        >
                          <i className="bx bx-envelope" aria-hidden="true" />
                          <span><EmailText email={playerInfo.email} /></span>
                        </a>
                      ) : (
                        <FactLine icon="bx-envelope" muted>Email not set</FactLine>
                      )}
                      {playerInfo.phone ? (
                        <FactLine icon="bx-phone">{playerInfo.phone}</FactLine>
                      ) : (
                        <FactLine icon="bx-phone" muted>Phone not set</FactLine>
                      )}
                      {playerInfo.location?.name ? (
                        <FactLine icon="bx-map">{playerInfo.location.name}</FactLine>
                      ) : (
                        <FactLine icon="bx-map" muted>Location not set</FactLine>
                      )}
                    </HeaderFact>
                    <HeaderFact label="Personal">
                      <div className="player-profile-fields">
                        <HeaderField label="Birth date">
                          {valueOrNull(playerInfo.birthDate, date => moment(date).format("D MMM YYYY"))}
                        </HeaderField>
                        <HeaderField label="Gender">{valueOrNull(playerInfo.gender, capitalize)}</HeaderField>
                        <HeaderField label="Dominant hand">{valueOrNull(playerInfo.dominantHand, capitalize)}</HeaderField>
                        <HeaderField label="T-shirt size">{valueOrNull(playerInfo.tShirtSize, size => size.toUpperCase())}</HeaderField>
                      </div>
                    </HeaderFact>
                    <HeaderFact label="Account">
                      <div className="player-profile-fields">
                        <HeaderField label="Language">{valueOrNull(playerInfo.language, language => language.toUpperCase())}</HeaderField>
                        <HeaderField label="Newsletter">
                          {playerInfo?.communicationPreferences?.newsletter ? "Yes" : "No"}
                        </HeaderField>
                        <HeaderField label="Organizer">{playerInfo.isOrganizer ? "Yes" : "No"}</HeaderField>
                        <HeaderField label="Reported">
                          <ReportCount userId={currentPlayerUserId} />
                        </HeaderField>
                      </div>
                    </HeaderFact>
                    <HeaderFact label="Membership & activity">
                      <div className="player-profile-fields">
                        <HeaderField label="Membership">{valueOrNull(playerInfo.membership?.plan, capitalize)}</HeaderField>
                        <HeaderField label="Balance">
                          {playerInfo.walletBalance} {playerInfo.currency}
                        </HeaderField>
                        {/* a parent who doesn't play has no player reputation: the children instead */}
                        {holdsPlayer ? (
                          <HeaderField label="Player reputation · All sports" wide>
                            <PlayerReputationSummary value={playerInfo?.ratingInfo} />
                            {props.userRole === "superAdmin" && (
                              <button type="button" className="btn btn-link player-profile-fact-link d-block" onClick={openRatingsModal}>
                                View individual ratings
                              </button>
                            )}
                          </HeaderField>
                        ) : (
                          <HeaderField label="Children" wide>{childrenSummary}</HeaderField>
                        )}
                      </div>
                    </HeaderFact>
                  </div>
                </CardBody>
              </Card>

              <Row>
                <Col xl="12">
                  {currentPlayerAccountId && playerInfo.status === "pending" && <PlayerInvitationPanel
                    key={props.actorId + ":" + currentPlayerAccountId} id={currentPlayerAccountId} actorId={props.actorId} />}
                  {/* the rewards and the network are the account's (anyone can invite); the availability is a player's */}
                  <ProfileOverviewCard
                    showAvailability={holdsPlayer}
                    availability={playerInfo?.availability}
                    currency={
                      firstTaskRewards.results?.[0]?.currency ??
                      referralRewards.results?.[0]?.currency
                    }
                    invitationCount={referralRewards.results?.length ?? 0}
                    invitationLimit={
                      settings?.rewardsInfo?.currencies?.[
                        playerInfo?.currency
                      ]?.maxRewards ?? settings?.rewardsInfo?.maxRewards
                    }
                    invitationRewards={totalReferralRewards}
                    networkSize={playerInfo?.totalReferredAccounts}
                    ownRewards={totalFirstTaskRewards}
                    totalRewards={
                      totalReferralRewards + totalFirstTaskRewards
                    }
                  />
                </Col>

                <Col xs={12}>
                  <Card>
                    <CardBody>
                      {/* PRD 16 LR16 (LR10 verdict U-B): the tabs follow the account's personas; the labels show on a phone too */}
                      <Nav tabs className="nav-tabs-custom">
                        {userTabs.map(tab => (
                          <NavItem key={tab.id}>
                            <NavLink
                              className={classnames({ active: shownTab === tab.id })}
                              onClick={() => toggleTab(tab.id)}
                            >
                              {tab.label}
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>

                      <TabContent activeTab={shownTab}>
                        {userTabs[0].id === "children" && <TabPane tabId="children" className="pt-3">
                          <Tenis10ChildrenPanel key={currentPlayerAccountId} accountId={currentPlayerAccountId} read={childrenRead} embedded />
                        </TabPane>}
                        <TabPane tabId="activity" className="pt-3">
                          <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-md-between mb-3">
                            <div>
                              <CardTitle className="font-size-15 mb-1">
                                Activity log
                              </CardTitle>
                              <p className="text-muted mb-2 mb-md-0">
                                Successful account actions recorded by the
                                platform, newest first.
                              </p>
                            </div>
                            <span className="badge badge-soft-secondary font-size-12 px-2 py-2">
                              {playerActivity?.totalResults || 0}{" "}
                              {(playerActivity?.totalResults || 0) === 1
                                ? "record"
                                : "records"}
                            </span>
                          </div>
                          <PaginationProvider
                            pagination={paginationFactory(
                              pagePlayerActivityOptions
                            )}
                          >
                            {({ paginationProps, paginationTableProps }) => (
                              <ToolkitProvider
                                keyField="_id"
                                data={
                                  isEmpty(playerActivity)
                                    ? []
                                    : playerActivity?.results
                                }
                                columns={activityColumns()}
                                bootstrap4
                              >
                                {toolkitProps => (
                                  <React.Fragment>
                                    <Row>
                                      <Col xl="12">
                                        <div className="table-responsive">
                                          <BootstrapTable
                                            responsive
                                            remote
                                            bordered={false}
                                            striped={false}
                                            classes={
                                              "table table-centered table-hover"
                                            }
                                            headerWrapperClasses={"thead-light"}
                                            noDataIndication="No activity has been recorded for this account."
                                            {...toolkitProps.baseProps}
                                            onTableChange={
                                              handleActivityTableChange
                                            }
                                            {...paginationTableProps}
                                          />
                                        </div>
                                      </Col>
                                    </Row>
                                    <Row className="align-items-md-center mt-30">
                                      <Col className="pagination pagination-rounded justify-content-center mb-2 inner-custom-pagination">
                                        <PaginationListStandalone
                                          {...paginationProps}
                                        />
                                      </Col>
                                    </Row>
                                  </React.Fragment>
                                )}
                              </ToolkitProvider>
                            )}
                          </PaginationProvider>
                        </TabPane>
                        <TabPane tabId="entries" className="pt-3">
                          <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-md-between mb-3">
                            <div>
                              <CardTitle className="font-size-15 mb-1">
                                Tournament participation
                              </CardTitle>
                              <p className="text-muted mb-2 mb-md-0">
                                Registration and payment responsibility for
                                this player. Doubles amounts are team totals;
                                payment status is separate from registration
                                status.
                              </p>
                            </div>
                            <div className="d-flex flex-wrap align-items-center mt-2 mt-md-0">
                              <span className="badge badge-soft-success font-size-12 px-2 py-2 mr-2 mb-1">
                                {entriesCountText(
                                  currentEntriesCount,
                                  "Active & upcoming"
                                )}
                              </span>
                              <span className="badge badge-soft-secondary font-size-12 px-2 py-2 mb-1">
                                {entriesCountText(historyEntriesCount, "Past")}
                              </span>
                            </div>
                          </div>
                          <PaginationProvider
                            pagination={paginationFactory(
                              pageTournamentEntriesOptions
                            )}
                          >
                            {({ paginationProps, paginationTableProps }) => (
                                <ToolkitProvider
                                  keyField="rowKey"
                                  data={tournamentEntries}
                                  columns={tournamentColumns}
                                  bootstrap4
                                >
                                  {toolkitProps => (
                                    <React.Fragment>
                                      <Row>
                                        <Col xl="12">
                                          <div className="table-responsive">
                                            <BootstrapTable
                                              responsive
                                              bordered={false}
                                              striped={false}
                                              classes={
                                                "table table-centered table-hover"
                                              }
                                              headerWrapperClasses={
                                                "thead-light"
                                              }
                                              noDataIndication="No tournament entries have been recorded for this player."
                                              rowClasses={
                                                getTournamentEntryRowClass
                                              }
                                              {...toolkitProps.baseProps}
                                              {...paginationTableProps}
                                            />
                                          </div>
                                        </Col>
                                      </Row>
                                      <Row className="align-items-md-center mt-30">
                                        <Col className="pagination pagination-rounded justify-content-center mb-2 inner-custom-pagination">
                                          <PaginationListStandalone
                                            {...paginationProps}
                                          />
                                        </Col>
                                      </Row>
                                    </React.Fragment>
                                  )}
                                </ToolkitProvider>
                              )}
                          </PaginationProvider>
                        </TabPane>
                        {/* membership tab */}
                        <TabPane tabId="membership" className="pt-3">
                          <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-md-between mb-3">
                            <div>
                              <CardTitle className="font-size-15 mb-1">
                                Membership
                              </CardTitle>
                              <p className="text-muted mb-0">
                                Review the current entitlement, apply a plan,
                                and audit how each membership was obtained.
                              </p>
                            </div>
                            <span className="badge badge-soft-secondary font-size-12 px-2 py-2 mt-2 mt-md-0">
                              {membershipList?.totalResults || 0}{" "}
                              {(membershipList?.totalResults || 0) === 1
                                ? "history record"
                                : "history records"}
                            </span>
                          </div>

                          <div className="bg-light rounded p-3 mb-3">
                            <Row className="align-items-end">
                              <Col lg="4" className="mb-3 mb-lg-0">
                                <div className="text-muted text-uppercase font-size-11 font-weight-medium mb-2">
                                  Current membership
                                </div>
                                <div className="d-flex align-items-center flex-wrap mb-2">
                                  <h5 className="mb-0 mr-2">
                                    {capitalize(currentMembershipPlan)}
                                  </h5>
                                  <span
                                    className={`badge badge-soft-${
                                      membershipIsActive
                                        ? "success"
                                        : "secondary"
                                    } px-2 py-1`}
                                  >
                                    {membershipStatus}
                                  </span>
                                </div>
                                {membershipEndDate ? (
                                  <div className="text-muted font-size-12">
                                    Valid until{" "}
                                    <span className="font-weight-semibold text-body">
                                      {moment(membershipEndDate).format(
                                        "D MMM YYYY"
                                      )}
                                    </span>
                                    {membershipDaysRemaining !== null && (
                                      <div className="mt-1">
                                        {membershipDaysRemaining} days remaining
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-muted font-size-12">
                                    No premium expiry date
                                  </div>
                                )}
                              </Col>

                              <Col lg="8">
                                <Label
                                  className="font-size-12"
                                  for="membershipEdit"
                                >
                                  Apply or extend membership
                                </Label>
                                <div className="d-flex flex-column flex-md-row align-items-md-end">
                                  <div className="flex-grow-1 mr-md-3">
                                    <Select styles={withFloatingSurface()}
                                      inputId="membershipEdit"
                                      name="membershipEdit"
                                      onChange={setEditMemberShip}
                                      options={membershipToChoose || []}
                                      placeholder="Choose a membership plan"
                                      value={editMemberShip}
                                    />
                                  </div>
                                  <Button
                                    className="mt-2 mt-md-0"
                                    color="primary"
                                    disabled={!editMemberShip || isLoading}
                                    onClick={handleUpdateMembership}
                                  >
                                    Apply membership
                                  </Button>
                                </div>
                                <div className="text-muted font-size-11 mt-2">
                                  Applying a premium plan extends an active
                                  entitlement. Administrator-applied plans are
                                  recorded with no player charge.
                                </div>
                              </Col>
                            </Row>
                          </div>

                          <div className="font-weight-semibold mb-2">
                            Membership history
                          </div>
                          <PaginationProvider
                            pagination={paginationFactory(
                              pageMembershipOptions
                            )}
                          >
                            {({ paginationProps, paginationTableProps }) => (
                              <ToolkitProvider
                                keyField="id"
                                data={membershipList?.results || []}
                                columns={membershipColumns()}
                                bootstrap4
                              >
                                {toolkitProps => (
                                  <>
                                    <Row>
                                      <Col xl="12">
                                        <div className="table-responsive">
                                          <BootstrapTable
                                            responsive
                                            remote
                                            bordered={false}
                                            striped={false}
                                            classes={
                                              "table table-centered table-hover"
                                            }
                                            headerWrapperClasses={"thead-light"}
                                            noDataIndication="No membership history has been recorded for this player."
                                            {...toolkitProps.baseProps}
                                            onTableChange={
                                              handleMembershipTableChange
                                            }
                                            {...paginationTableProps}
                                          />
                                        </div>
                                      </Col>
                                    </Row>
                                    <Row className="align-items-md-center mt-30">
                                      <Col className="pagination pagination-rounded justify-content-center mb-2 inner-custom-pagination">
                                        <PaginationListStandalone
                                          {...paginationProps}
                                        />
                                      </Col>
                                    </Row>
                                  </>
                                )}
                              </ToolkitProvider>
                            )}
                          </PaginationProvider>
                        </TabPane>
                        {/* orders */}
                        <TabPane tabId="orders" className="pt-3">
                          <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-md-between mb-3">
                            <div>
                              <CardTitle className="font-size-15 mb-1">
                                Orders & payments
                              </CardTitle>
                              <p className="text-muted mb-0">
                                Transactions charged to or credited to this
                                account. Doubles payments show the team total
                                and covered partner; partner-paid registrations
                                are identified under Tournament Entries.
                              </p>
                            </div>
                            <div className="d-flex align-items-center mt-2 mt-md-0 ml-md-3">
                              <Label
                                className="text-muted font-size-12 mb-0 mr-2"
                                for="orderCategoryFilter"
                              >
                                Show
                              </Label>
                              <select
                                className="custom-select custom-select-sm"
                                id="orderCategoryFilter"
                                onChange={handleTransactionCategoryChange}
                                value={transactionCategory}
                              >
                                {orderCategoryOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <span className="badge badge-soft-secondary font-size-12 px-2 py-2 ml-2">
                                {playerTransactions?.totalResults || 0}{" "}
                                {(playerTransactions?.totalResults || 0) === 1
                                  ? "transaction"
                                  : "transactions"}
                              </span>
                            </div>
                          </div>
                          <PaginationProvider
                            pagination={paginationFactory(
                              pageTransactionsOptions
                            )}
                          >
                            {({ paginationProps, paginationTableProps }) => (
                              <ToolkitProvider
                                keyField="id"
                                data={playerTransactions?.results || []}
                                columns={orderColumns({ accountChildren: accountRows || [] })}
                                bootstrap4
                              >
                                {toolkitProps => (
                                  <>
                                    <Row>
                                      <Col xl="12">
                                        <div className="table-responsive">
                                          <BootstrapTable
                                            responsive
                                            remote
                                            bordered={false}
                                            striped={false}
                                            classes={
                                              "table table-centered table-hover"
                                            }
                                            headerWrapperClasses={"thead-light"}
                                            noDataIndication="No financial transactions have been recorded for this player."
                                            {...toolkitProps.baseProps}
                                            onTableChange={
                                              handleTransactionTableChange
                                            }
                                            {...paginationTableProps}
                                          />
                                        </div>
                                      </Col>
                                    </Row>
                                    <Row className="align-items-md-center mt-30">
                                      <Col className="pagination pagination-rounded justify-content-center mb-2 inner-custom-pagination">
                                        <PaginationListStandalone
                                          {...paginationProps}
                                        />
                                      </Col>
                                    </Row>
                                  </>
                                )}
                              </ToolkitProvider>
                            )}
                          </PaginationProvider>
                        </TabPane>
                        {/* wallet */}
                        <TabPane tabId="wallet" className="pt-3">
                          <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-md-between mb-3">
                            <div>
                              <CardTitle className="font-size-15 mb-1">
                                Wallet
                              </CardTitle>
                              <p className="text-muted mb-0">
                                Apply a documented balance correction and audit
                                every transaction affecting this wallet.
                              </p>
                            </div>
                            <span className="badge badge-soft-secondary font-size-12 px-2 py-2 mt-2 mt-md-0">
                              {playerWalletTransactions?.totalResults || 0}{" "}
                              {(playerWalletTransactions?.totalResults || 0) ===
                              1
                                ? "wallet transaction"
                                : "wallet transactions"}
                            </span>
                          </div>

                          <AvForm onValidSubmit={handleBalanceSubmit}>
                            <div className="bg-light rounded p-3 mb-3">
                              <Row>
                                <Col lg="3" className="mb-3 mb-lg-0">
                                  <div className="text-muted text-uppercase font-size-11 font-weight-medium mb-2">
                                    Current balance
                                  </div>
                                  <h4 className="mb-1">
                                    {playerInfo?.walletBalance ?? 0}{" "}
                                    {playerInfo.currency}
                                  </h4>
                                  <div className="text-muted font-size-12">
                                    Result after adjustment:{" "}
                                    <span className="font-weight-semibold text-body">
                                      {walletBalancePreview}{" "}
                                      {playerInfo.currency}
                                    </span>
                                  </div>
                                </Col>

                                <Col lg="9">
                                  <Row>
                                    <Col md="3">
                                      <FormGroup>
                                        <AvField
                                          label="Operation"
                                          name="walletOperation"
                                          onChange={event =>
                                            setWalletOperation(
                                              event.target.value
                                            )
                                          }
                                          type="select"
                                          value={walletOperation}
                                        >
                                          <option value="add">Add funds</option>
                                          <option value="subtract">
                                            Remove funds
                                          </option>
                                        </AvField>
                                      </FormGroup>
                                    </Col>
                                    <Col md="4">
                                      <FormGroup>
                                        <AvField
                                          label="Amount"
                                          name="selectWalletBalance"
                                          onChange={handleWalletBalance}
                                          type="select"
                                          value={walletBalance}
                                        >
                                          <option value="">
                                            Choose an amount
                                          </option>
                                          {settings?.topUpOptions
                                            ?.filter(
                                              option =>
                                                option?.amount?.[
                                                  playerInfo.currency
                                                ]
                                            )
                                            ?.map((option, key) => (
                                              <option
                                                value={
                                                  option.amount[
                                                    playerInfo.currency
                                                  ]
                                                }
                                                key={key}
                                              >
                                                {
                                                  option.amount[
                                                    playerInfo.currency
                                                  ]
                                                }{" "}
                                                {playerInfo.currency}
                                              </option>
                                            ))}
                                          <option value="custom">
                                            Custom amount
                                          </option>
                                        </AvField>
                                      </FormGroup>
                                    </Col>
                                    <Col md="5">
                                      {walletBalance === "custom" ? (
                                        <AvField
                                          errorMessage="Enter an amount greater than zero"
                                          label={`Custom amount (${playerInfo.currency})`}
                                          min="0.01"
                                          name="customWalletBalance"
                                          onChange={handleCustomWalletBalance}
                                          placeholder="0.00"
                                          step="0.01"
                                          type="number"
                                          validate={{
                                            min: {
                                              value: 0.01,
                                              errorMessage:
                                                "Enter an amount greater than zero",
                                            },
                                            required: { value: true },
                                          }}
                                          value={walletAmount}
                                        />
                                      ) : (
                                        <div className="pt-md-4 mt-md-2 text-muted font-size-12">
                                          Enter amounts as positive values; the
                                          selected operation determines the
                                          direction.
                                        </div>
                                      )}
                                    </Col>
                                  </Row>
                                  <Row className="align-items-end">
                                    <Col md="8">
                                      <FormGroup className="mb-md-0">
                                        <AvField
                                          label="Reason for adjustment"
                                          name="addNotes"
                                          placeholder="Required for the audit trail"
                                          type="text"
                                          validate={{
                                            required: {
                                              value: true,
                                              errorMessage:
                                                "Explain why this balance is being adjusted",
                                            },
                                          }}
                                        />
                                      </FormGroup>
                                    </Col>
                                    <Col md="4" className="text-md-right">
                                      <Button
                                        color={
                                          walletOperation === "subtract"
                                            ? "danger"
                                            : "primary"
                                        }
                                        disabled={
                                          isLoading ||
                                          walletAdjustmentAmount === null
                                        }
                                        type="submit"
                                      >
                                        {walletOperation === "subtract"
                                          ? "Remove funds"
                                          : "Add funds"}
                                      </Button>
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>
                              <div className="text-muted font-size-11 mt-2">
                                This action updates the balance immediately,
                                notifies the player, and records the responsible
                                administrator.
                              </div>
                            </div>
                          </AvForm>

                          <div className="font-weight-semibold mb-2">
                            Wallet ledger
                          </div>
                          <PaginationProvider
                            pagination={paginationFactory(
                              pageWalletTransactionsOptions
                            )}
                          >
                            {({ paginationProps, paginationTableProps }) => (
                              <ToolkitProvider
                                keyField="id"
                                data={playerWalletTransactions?.results || []}
                                columns={orderColumns({ showBalance: true, accountChildren: accountRows || [] })}
                                bootstrap4
                              >
                                {toolkitProps => (
                                  <>
                                    <Row>
                                      <Col xl="12">
                                        <div className="table-responsive">
                                          <BootstrapTable
                                            responsive
                                            remote
                                            bordered={false}
                                            striped={false}
                                            classes={
                                              "table table-centered table-hover"
                                            }
                                            headerWrapperClasses={"thead-light"}
                                            noDataIndication="No wallet transactions have been recorded for this player."
                                            {...toolkitProps.baseProps}
                                            onTableChange={handleTableChange}
                                            {...paginationTableProps}
                                          />
                                        </div>
                                      </Col>
                                    </Row>
                                    <Row className="align-items-md-center mt-30">
                                      <Col className="pagination pagination-rounded justify-content-center mb-2 inner-custom-pagination">
                                        <PaginationListStandalone
                                          {...paginationProps}
                                        />
                                      </Col>
                                    </Row>
                                  </>
                                )}
                              </ToolkitProvider>
                            )}
                          </PaginationProvider>
                        </TabPane>

                        {/* the player's own blocks, as they were on the page (LR10 verdict U-B): the reviews, the levels and
                            rankings, the competitive levels (the former „Level change” tab) */}
                        {holdsPlayer && <TabPane tabId="player" className="pt-3">
                          <ReviewAudit compact actorId={props.actorId} playerId={currentPlayerUserId} mode="received" canView={props.userRole === 'superAdmin'} />
                          <ReviewAudit compact actorId={props.actorId} playerId={currentPlayerUserId} canView={props.userRole === 'superAdmin'} />
                          <SportsSummaryCard
                            activeSports={settings?.activeSports || []}
                            sports={playerInfo?.sports}
                          />
                          <Card><CardBody>
                          <div className="d-flex flex-column flex-md-row align-items-md-start justify-content-md-between mb-3">
                            <div>
                              <CardTitle className="font-size-15 mb-1">
                                Competitive levels
                              </CardTitle>
                              <p className="text-muted mb-0">
                                Compare the current sport levels, apply a
                                deliberate change, and review the audit trail.
                              </p>
                            </div>
                            <span className="badge badge-soft-secondary font-size-12 px-2 py-2 mt-2 mt-md-0">
                              {levelChange?.totalResults || 0}{" "}
                              {(levelChange?.totalResults || 0) === 1
                                ? "level change"
                                : "level changes"}
                            </span>
                          </div>

                          <div className="bg-light rounded p-3 mb-3">
                            <Row className="align-items-start">
                              <Col lg="3" md="6" className="mb-3 mb-lg-0">
                                <Label className="font-size-12" for="sportType">
                                  Sport
                                </Label>
                                <Select styles={withFloatingSurface()}
                                  inputId="sportType"
                                  value={{
                                    label: capitalize(sportType),
                                    value: sportType,
                                  }}
                                  onChange={e => setSportType(e.value)}
                                  options={settings?.activeSports?.map(
                                    sport => ({
                                      label: capitalize(sport),
                                      value: sport,
                                    })
                                  )}
                                />
                                <div className="text-muted font-size-11 mt-1">
                                  Editing {capitalize(sportType)} profile
                                </div>
                              </Col>
                              <Col lg="3" md="6" className="mb-3 mb-lg-0">
                                <Label
                                  className="font-size-12"
                                  for="singlesLevel"
                                >
                                  Singles level
                                </Label>
                                <Select styles={withFloatingSurface()}
                                  inputId="singlesLevel"
                                  value={{
                                    label:
                                      selectedSoloLevel === undefined ||
                                      selectedSoloLevel === null
                                        ? "Not set"
                                        : `Level ${selectedSoloLevel}`,
                                    value: selectedSoloLevel,
                                  }}
                                  onChange={e =>
                                    setSelectedSoloLevel(e.value)
                                  }
                                  options={settings?.levels?.map(level => ({
                                    label: `Level ${level?.key}`,
                                    value: level?.key,
                                  }))}
                                />
                                <div className="text-muted font-size-11 mt-1">
                                  Current: {currentSinglesLevel ?? "Not set"}
                                </div>
                              </Col>
                              <Col lg="3" md="6" className="mb-3 mb-lg-0">
                                <Label
                                  className="font-size-12"
                                  for="doublesLevel"
                                >
                                  Doubles level
                                </Label>
                                <Select styles={withFloatingSurface()}
                                  inputId="doublesLevel"
                                  value={{
                                    label:
                                      selectedDuoLevel === undefined ||
                                      selectedDuoLevel === null
                                        ? "Not set"
                                        : `Level ${selectedDuoLevel}`,
                                    value: selectedDuoLevel,
                                  }}
                                  onChange={e => setSelectedDuoLevel(e.value)}
                                  options={settings?.levels?.map(level => ({
                                    label: `Level ${level?.key}`,
                                    value: level?.key,
                                  }))}
                                />
                                <div className="text-muted font-size-11 mt-1">
                                  Current: {currentDoublesLevel ?? "Not set"}
                                </div>
                              </Col>
                              <Col lg="3" md="6">
                                <Label
                                  className="font-size-12"
                                  for="saveLevelChanges"
                                >
                                  Action
                                </Label>
                                <Button
                                  block
                                  id="saveLevelChanges"
                                  onClick={() => {
                                    if (hasOwnActiveEntries) {
                                      setActiveCompetitonRegisteredAlert(true)
                                    } else {
                                      handleLevelChange()
                                    }
                                  }}
                                  color="primary"
                                  disabled={!levelHasChanges || isLoading}
                                >
                                  Save level changes
                                </Button>
                                <div className="text-muted font-size-11 mt-1">
                                  {levelHasChanges
                                    ? "Changes ready to save"
                                    : "No unsaved changes"}
                                </div>
                              </Col>
                            </Row>
                            <div className="d-flex flex-column flex-md-row justify-content-between text-muted font-size-11 mt-3 pt-2 border-top">
                              <span>
                                The player is notified after an administrator
                                changes their level.
                              </span>
                              {hasOwnActiveEntries && (
                                <span className="text-warning mt-1 mt-md-0">
                                  This player has active tournament entries;
                                  confirmation will be required.
                                </span>
                              )}
                            </div>
                          </div>

                          {props.userRole === 'superAdmin' && <OpponentLevelAnalysis
                            key={props.actorId + ":" + currentPlayerUserId}
                            playerUserId={currentPlayerUserId} sportType={sportType} canView />}

                          <div className="font-weight-semibold mb-2">
                            Level change history
                          </div>
                          <PaginationProvider
                            pagination={paginationFactory(
                              pageLevelChangeOptions
                            )}
                          >
                            {({ paginationProps, paginationTableProps }) => (
                              <ToolkitProvider
                                keyField="id"
                                data={
                                  isEmpty(levelChange)
                                    ? []
                                    : levelChange?.results
                                }
                                columns={levelChangeColumns()}
                                bootstrap4
                              >
                                {toolkitProps => (
                                  <>
                                    <Row>
                                      <Col xl="12">
                                        <div className="table-responsive">
                                          <BootstrapTable
                                            responsive
                                            remote
                                            bordered={false}
                                            striped={false}
                                            classes={
                                              "table table-centered table-hover"
                                            }
                                            headerWrapperClasses={"thead-light"}
                                            noDataIndication="No administrator level changes have been recorded for this player."
                                            {...toolkitProps.baseProps}
                                            onTableChange={
                                              handleLevelChangeChange
                                            }
                                            {...paginationTableProps}
                                          />
                                        </div>
                                      </Col>
                                    </Row>
                                    <Row className="align-items-md-center mt-30">
                                      <Col className="pagination pagination-rounded justify-content-center mb-2 inner-custom-pagination">
                                        <PaginationListStandalone
                                          {...paginationProps}
                                        />
                                      </Col>
                                    </Row>
                                  </>
                                )}
                              </ToolkitProvider>
                            )}
                          </PaginationProvider>
                          </CardBody></Card>
                        </TabPane>}
                      </TabContent>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
          <RatingsModal
            key={props.actorId + ":" + currentPlayerUserId}
            actorId={props.actorId}
            canViewRatings={props.userRole === "superAdmin"}
            playerId={currentPlayerUserId}
            viewRatingsModal={viewRatingsModal}
            onClose={closeRatingsModal}
          />
        </Container>

        {activeCompetitonRegisteredAlert ? (
          <SweetAlert
            warning
            title="Level change"
            showCancel
            onConfirm={() => {
              handleLevelChange()
              setActiveCompetitonRegisteredAlert(false)
            }}
            onCancel={() => {
              setActiveCompetitonRegisteredAlert(false)
            }}
          >
            <h5>
              This player is registered to active competitions. Are you sure you
              want to change its level?
            </h5>
          </SweetAlert>
        ) : null}

        {successAlert ? (
          <SweetAlert
            success
            title={alertTitle || "Success"}
            onConfirm={() => {
              setSuccessAlert(false)
            }}
          >
            {alertDescription}
          </SweetAlert>
        ) : null}

        {blockAlert ? (
          <SweetAlert
            title="Are you sure?"
            warning
            showCancel
            confirmButtonText="Yes, block it!"
            confirmBtnBsStyle="success"
            cancelBtnBsStyle="danger"
            onConfirm={handleBlock}
            onCancel={() => setBlockAlert(false)}
          >
            You won't be able to revert this!
          </SweetAlert>
        ) : null}

        {deleteAlert ? (
          <SweetAlert
            title="Are you sure?"
            warning
            showCancel
            confirmButtonText="Yes, block it!"
            confirmBtnBsStyle="success"
            cancelBtnBsStyle="danger"
            onConfirm={handleDelete}
            onCancel={() => setDeleteAlert(false)}
          >
            You won't be able to revert this!
          </SweetAlert>
        ) : null}

        {activateAlert ? (
          <SweetAlert
            title="Are you sure?"
            warning
            showCancel
            confirmButtonText="Yes, activate it!"
            confirmBtnBsStyle="success"
            cancelBtnBsStyle="danger"
            onConfirm={handleActivate}
            onCancel={() => setActivateAlert(false)}
          >
            You won't be able to revert this!
          </SweetAlert>
        ) : null}
      </div>
    </React.Fragment>
  )
}

PlayerProfile.propTypes = {
  userRole: PropTypes.string,
  playerInfo: PropTypes.object,
  getPlayerInfo: PropTypes.func,
  match: PropTypes.object,
}

const mapStateToProps = ({
  players,
  session,
  session: { userDetails } = {},
}) => ({
  loading: players.profileLoading,
  profileError: players.profileError,
  playerInfo: players.playerInfo,
  playerTransactions: players.playerTransactions,
  referralRewards: players.referralRewards,
  firstTaskRewards: players.firstTaskRewards,
  playerWalletTransactions: players.playerWalletTransactions,
  membershipList: players.membershipList,
  playerActivity: players.playerActivity,
  levelChange: players.levelChange,
  memberships: session?.settings?.memberships,
  playerEntries: players?.playerEntries,
  playerHistoryEntries: players?.playerHistoryEntries,
  settings: session.settings,
  userId: userDetails.userId,
  userRole: userDetails.role,
  actorId: userDetails.id || userDetails._id,
  impersonateKey: userDetails.impersonateKey,
})

const mapDispatchToProps = dispatch => ({
  getPlayerInfo: id => dispatch(getPlayerInfo(id)),
  getPlayerTransactions: (id, transactionsOptions) =>
    dispatch(getPlayerTransactions(id, transactionsOptions)),
  getPlayerWalletTransactions: (id, options) =>
    dispatch(getPlayerWalletTransactions(id, options)),
  getMembershipList: (id, membershipOptions) =>
    dispatch(getMembershipList(id, membershipOptions)),
  getPlayerActivity: (id, playerActivityOptions) =>
    dispatch(getPlayerActivity(id, playerActivityOptions)),
  getLevelChange: (id, levelChangeOptions) =>
    dispatch(getLevelChange(id, levelChangeOptions)),
  fetchPlayerEntries: (id, playerEntriesOptions) =>
    dispatch(getPlayerEntries(id, playerEntriesOptions)),
  fetchPlayerHistoryEntries: (id, playerHistoryEntriesOptions) =>
    dispatch(getPlayerHistoryEntries(id, playerHistoryEntriesOptions)),
  fetchPlayerReferralRewards: (id) =>
    dispatch(getPlayerReferralRewards(id)),
  fetchPlayerFirstTaskRewards: (id) =>
    dispatch(getPlayerFirstTaskRewards(id)),
  resetPlayerProfileData: () => dispatch(resetPlayerProfileData()),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(PlayerProfile))
